import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { supabase } from '@/lib/supabase';
import { User } from '@/models/User';
import { refreshAccessToken } from '@/api/googleApi';
/**
 * @typedef {import('@/types/auth/User').User} User
 */

/**
 * 認証状態およびユーザー情報を管理するストア
 * * Supabase Auth と連携し、ログインユーザーのセッション維持、
 * * 認証状態の変化の監視、および初期ロード状態の管理を行います。
 * @property {import('vue').Ref<User | null>} user - 現在ログイン中のユーザーオブジェクト
 * @property {import('vue').ComputedRef<boolean>} isAdmin - 管理者かどうか
 * @property {import('vue').Ref<boolean>} authLoading - 初期セッション確認中かどうかのフラグ
 * @property {import('vue').ComputedRef<boolean>} isAuthenticated - ログイン済みかどうか
 * @example
 * const auth = useAuthStore();
 * console.log(auth.user?.username);
 */
export const useAuthStore = defineStore('auth', () => {
  // --- State ---
  /**
   * アプリ全体で共有するログインユーザーの情報
   *  @type {import('vue').Ref<User | null>} */
  const user = ref(null);

  // --- provider_token を保持する ---
  const providerToken = ref(null);

  /**
   * ロード状態フラグ
   * @type {import('vue').Ref<Boolean>} */
  const authLoading = ref(true);

  // --- Set ---
  const setUser = async (session) => {
    // セッション全体を受け取って処理する形に変更
    user.value = session?.user ? new User(session.user) : null;
    if (!user.value) {
      return;
    }
    if (user.value.provider !== 'google') {
      console.warn(
        'Google以外のプロバイダーでログインしています。Googleカレンダー機能は利用できません',
      );
      return;
    }
    // セッションから Google のアクセストークンを抽出
    providerToken.value = session?.provider_token ?? null;

    // 存在しない場合DBから取得する
    if (!providerToken.value) {
      console.log('ユーザーリフレッシュトークン取得開始......');

      // DBからリフレッシュトークンを取得し、アクセストークンを発行する
      providerToken.value = await refreshAccessToken();
    }
  };

  /**
   * 管理者かどうかを返す
   * @returns {boolean} 管理者かどうか
   */
  const isAdmin = computed(() => {
    return user.value?.isAdmin ?? false;
  });

  // --- Getters ---
  /**
   * ログイン状態の判定
   * @type {ImportAttributes('vue').ComputedRef<Boolean>}
   */
  const isAuthenticated = computed(() => {
    return !!user.value; //真偽値に変換
  });

  // --- 初期化ロジック ---
  // 初期化を Promise として定義
  // 初回読み込み時に Supabase のセッションを復元
  const initPromise = (async () => {
    console.log('初期化処理開始...');
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('セッション復元エラー：', error.message);
      setUser(null);
    } else {
      setUser(data.session);
    }
    console.log('初期化処理完了');
    return new Promise((resolve) => {
      setTimeout(() => {
        authLoading.value = false;
        resolve(user.value); // 完了を通知
      }, 500);
    });
  })();

  // ログイン・ログアウトなど認証状態の変化を監視
  let saving = false;

  supabase.auth.onAuthStateChange((event, session) => {
    console.log('認証状態:', event);
    setUser(session);

    if (saving) return;

    // リフレッシュトークンが存在する場合にdbに保存
    if (event === 'SIGNED_IN' && session?.provider_refresh_token) {
      saving = true;

      (async () => {
        try {
          const { data, error } = await supabase
            .from('user_api_keys')
            .upsert(
              {
                user_id: session.user.id,
                refresh_token: session.provider_refresh_token,
                provider: 'Google',
              },
              { onConflict: 'user_id,provider' }, //user_idとproviderの組み合わせで重複判断
            )
            .select();

          console.log('影響のあった行数', data.length);
          console.log('データ', data);

          if (error) console.error('トークン保存失敗:', error.message);
        } finally {
          saving = false;
        }
      })();
    }
  });

  return {
    user,
    providerToken,
    isAdmin,
    authLoading,
    isAuthenticated,
    initPromise,
  };
});
